import React, { useState, useEffect, useImperativeHandle } from "react";
import {
  createStyles,
  Table,
  ScrollArea,
  Select,
  UnstyledButton,
  Group,
  Text,
  Center,
  TextInput,
  Pagination,
  Space,
  Button,
  ActionIcon,
  Paper,
  HoverCard,
  Tooltip,
  Divider,
  Popover,
  Box,
} from "@mantine/core";
import {
  IconSelector,
  IconChevronDown,
  IconChevronUp,
  IconSearch,
  IconX,
  IconFilter,
  IconCheck,
} from "@tabler/icons";
import AdvancedFilterModal from "./advancedFilterModal";
import _ from "lodash";
import { useForceUpdate } from "@mantine/hooks";
import useDeepCompareEffect from "../hooks/useDeepCompareEffect";
import { useTranslation } from "react-i18next";
import { useServerApi } from "../hooks/userServerApi";
import { buildTeamUserFilterOptions } from "../helpers/consultantTeamFilter";
import { formatDisplayValue } from "../helpers/phoneUtils";

function getTableFilterSelectedValues(tableFilterQuery, col) {
  const field = col.filter?.field;
  if (!field || _.isEmpty(tableFilterQuery) || !_.has(tableFilterQuery, field)) {
    return null;
  }

  const query = tableFilterQuery[field];
  if (!query) return null;

  if (query.$in) return query.$in;

  if (query.$or && col.filter?.orFields?.length) {
    const primaryField = col.filter.orFields[0];
    const branch = query.$or.find((part) => part[primaryField]);
    return branch?.[primaryField]?.$in ?? [];
  }

  return [];
}

function buildUserFilterOptions(users, labelField = "name") {
  return buildTeamUserFilterOptions(users, labelField);
}

const useStyles = createStyles((theme) => ({
  scrollArea: {},

  scrolled: {
    boxShadow: theme.shadows.sm,
  },

  th: {},
  control: {},
  icon: {},

  tableCell: {
    verticalAlign: "top",
  },

  filterRow: {
    cursor: "pointer",
    borderRadius: theme.radius.sm,
    padding: "6px 10px",
    "&:hover": {
      backgroundColor:
        theme.colorScheme === "dark"
          ? theme.colors.dark[5]
          : theme.colors.gray[0],
    },
  },

  filterRowSelected: {
    backgroundColor:
      theme.colorScheme === "dark"
        ? theme.fn.rgba(theme.colors.teal[9], 0.25)
        : theme.fn.rgba(theme.colors.teal[0], 0.9),
  },
}));

function valuesEqual(a, b) {
  return _.isEqual(a, b);
}

function useColumnDistinctFilter(col, tableFilterQuery) {
  const [api] = useServerApi();
  const [possibleValues, setPossibleValues] = useState([]);
  const [selectedValues, setSelectedValues] = useState([]);
  const { t: translate } = useTranslation();

  const applyFilterList = (list) => {
    const normalized = Array.isArray(list) ? list : [];
    normalized.forEach((v) => {
      v.label = translate(v.label);
    });
    setPossibleValues(normalized);
    col.possibleValues = normalized;

    if (
      !_.isEmpty(tableFilterQuery) &&
      _.has(tableFilterQuery, col.filter.field)
    ) {
      const stored = getTableFilterSelectedValues(tableFilterQuery, col);
      setSelectedValues(stored ?? []);
    } else {
      setSelectedValues(normalized.map((v) => v?.value));
    }
  };

  const fetchUserPreQueryFilterValues = async () => {
    const populate = col.filter?.populate ?? {};
    const select = populate.select ?? "name cname staffId aliasName";
    const labelField = populate.labelField ?? "name";
    const sortField = Object.keys(populate.sort ?? { name: 1 })[0] ?? "name";

    const data = await api.search({
      apiEntity: "user",
      pageSize: 2000,
      currentPage: 1,
      sort: { by: sortField, order: "asc" },
      searchQuery: col.filter.userPreQuery,
      select,
      byAggregation: false,
    });

    applyFilterList(buildUserFilterOptions(data?.docs ?? [], labelField));
  };

  const fetchTeamUserFilterValues = async () => {
    const teamUserIds = col.filter?.teamUserIds ?? [];
    const populate = col.filter?.populate ?? {};
    const select = populate.select ?? "name cname staffId aliasName";
    const labelField = populate.labelField ?? "name";
    const sortField = Object.keys(populate.sort ?? { name: 1 })[0] ?? "name";

    const data = await api.search({
      apiEntity: "user",
      pageSize: Math.max(teamUserIds.length, 1),
      currentPage: 1,
      sort: { by: sortField, order: "asc" },
      searchQuery: { _id: { $in: teamUserIds } },
      select,
      byAggregation: false,
    });

    const users = data?.docs ?? [];
    const idOrder = new Map(teamUserIds.map((id, i) => [String(id), i]));
    users.sort(
      (a, b) =>
        (idOrder.get(String(a._id)) ?? 0) - (idOrder.get(String(b._id)) ?? 0),
    );
    applyFilterList(buildTeamUserFilterOptions(users, labelField));
  };

  const fetchDistinctValues = async () => {
    try {
      if (col.filter?.teamUserIds?.length) {
        await fetchTeamUserFilterValues();
        return;
      }

      if (col.filter?.userPreQuery) {
        await fetchUserPreQueryFilterValues();
        return;
      }

      const values = await api.getDistinctValues({
        apiEntity: col.filter?.apiEntity,
        field: col.filter?.field,
        populate: col.filter?.populate,
      });
      applyFilterList(values);
    } catch (e) {
      console.log(e);
    }
  };

  useDeepCompareEffect(() => {
    if (!col.filterable || !col.filter?.field) return;
    fetchDistinctValues();
  }, [col?.filter?.field, col?.filter?.teamUserIds, col?.filter?.userPreQuery]);

  useEffect(() => {
    if (!col.filterable || !col.filter?.field || !possibleValues.length) return;
    if (
      !_.isEmpty(tableFilterQuery) &&
      _.has(tableFilterQuery, col.filter.field)
    ) {
      const stored = getTableFilterSelectedValues(tableFilterQuery, col);
      setSelectedValues(stored ?? []);
    } else {
      setSelectedValues(possibleValues.map((v) => v?.value));
    }
  }, [tableFilterQuery, possibleValues, col.filter?.field, col.filterable]);

  return {
    possibleValues,
    selectedValues,
    setSelectedValues,
    translate,
  };
}

function useLocalDistinctFilter(col, tableFilterQuery, buildDistinct, dataSource) {
  const [possibleValues, setPossibleValues] = useState([]);
  const [selectedValues, setSelectedValues] = useState([]);
  const { t: translate } = useTranslation();

  const applyFilterList = (list) => {
    const normalized = Array.isArray(list) ? list : [];
    normalized.forEach((v) => {
      v.label = translate(v.label);
    });
    setPossibleValues(normalized);
    col.possibleValues = normalized;

    if (
      !_.isEmpty(tableFilterQuery) &&
      _.has(tableFilterQuery, col.filter.field)
    ) {
      const stored = getTableFilterSelectedValues(tableFilterQuery, col);
      setSelectedValues(stored ?? []);
    } else {
      setSelectedValues(normalized.map((v) => v?.value));
    }
  };

  useDeepCompareEffect(() => {
    if (!col.filterable || !col.filter?.field || !buildDistinct) return;
    const list = buildDistinct(dataSource, col);
    applyFilterList(list);
  }, [col?.filter?.field, dataSource, buildDistinct]);

  useEffect(() => {
    if (!col.filterable || !col.filter?.field || !possibleValues.length) return;
    if (
      !_.isEmpty(tableFilterQuery) &&
      _.has(tableFilterQuery, col.filter.field)
    ) {
      const stored = getTableFilterSelectedValues(tableFilterQuery, col);
      setSelectedValues(stored ?? []);
    } else {
      setSelectedValues(possibleValues.map((v) => v?.value));
    }
  }, [tableFilterQuery, possibleValues, col.filter?.field, col.filterable]);

  return {
    possibleValues,
    selectedValues,
    setSelectedValues,
    translate,
  };
}

function getChipButtonLabel(col, selectedValues, possibleValues, t) {
  const categoryLabel = t(col.headerName ?? _.capitalize(col.field));
  const filterLabel = col.filter?.label ? t(col.filter.label) : categoryLabel;

  const pv = possibleValues ?? [];
  const sv = selectedValues ?? [];

  if (!pv.length) return filterLabel;

  const allSelected = sv.length > 0 && sv.length === pv.length;
  const noneSelected = sv.length === 0;

  if (allSelected || noneSelected) {
    return filterLabel;
  }

  const firstVal = sv[0];
  const firstItem = pv.find((v) => valuesEqual(v.value, firstVal));
  const firstLabel = firstItem?.label ?? String(firstVal);
  const extra = sv.length - 1;
  if (extra <= 0) return firstLabel;
  return `${firstLabel} + ${extra}`;
}

function ColumnFilterPanel({
  col,
  onFilter,
  possibleValues,
  selectedValues,
  setSelectedValues,
}) {
  const { t } = useTranslation();
  const { classes, cx, theme } = useStyles();
  const [optionSearch, setOptionSearch] = useState("");

  const w = col.filter?.width ?? 260;
  const h = col.filter?.height ?? 280;

  useEffect(() => {
    setOptionSearch("");
  }, [possibleValues, col?.filter?.field]);

  const pv = possibleValues ?? [];
  const sv = selectedValues ?? [];

  const searchLower = optionSearch.trim().toLowerCase();
  const filteredValues = !searchLower
    ? pv
    : pv.filter((v) =>
        String(v.label ?? "")
          .toLowerCase()
          .includes(searchLower),
      );

  const isRowSelected = (val) => sv.some((s) => valuesEqual(s, val));

  const applyFilter = (next) => {
    setSelectedValues(next);
    onFilter(col.filter.field, next, col.possibleValues);
  };

  const toggleValue = (val) => {
    const next = sv.some((s) => valuesEqual(s, val))
      ? sv.filter((s) => !valuesEqual(s, val))
      : [...sv, val];
    applyFilter(next);
  };

  const selectAll = () => {
    const all = pv.map((v) => v.value);
    applyFilter(all);
  };

  const deselectAll = () => {
    applyFilter([]);
  };

  return (
    <Box p={0}>
      <Group position="apart" spacing="xs" px="sm" pt="xs" pb={4} noWrap>
        <UnstyledButton type="button" onClick={selectAll}>
          <Text size="xs" color="teal" weight={500}>
            {t("Select all")}
          </Text>
        </UnstyledButton>
        <UnstyledButton type="button" onClick={deselectAll}>
          <Text size="xs" color="dimmed" weight={500}>
            {t("Deselect all")}
          </Text>
        </UnstyledButton>
      </Group>
      <Divider />
      <Box px="sm" pt="xs" pb={4}>
        <TextInput
          size="xs"
          placeholder={t("Search")}
          icon={<IconSearch size={14} stroke={1.5} />}
          value={optionSearch}
          onChange={(e) => setOptionSearch(e.currentTarget.value)}
          rightSection={
            optionSearch ? (
              <UnstyledButton
                type="button"
                onClick={() => setOptionSearch("")}
                aria-label={t("Clear")}
              >
                <IconX size={14} stroke={1.5} />
              </UnstyledButton>
            ) : null
          }
        />
      </Box>
      <ScrollArea style={{ width: w, height: h }}>
        <Box py="xs" pr={4}>
          {filteredValues.length === 0 && (
            <Text size="xs" color="dimmed" px="sm" py="xs">
              {t("No matches")}
            </Text>
          )}
          {filteredValues.map((v, index) => {
            const selected = isRowSelected(v.value);
            return (
              <UnstyledButton
                key={`${col.filter?.field}-${JSON.stringify(v.value)}`}
                type="button"
                onClick={() => toggleValue(v.value)}
                className={cx(
                  classes.filterRow,
                  selected && classes.filterRowSelected,
                )}
                style={{ display: "block", width: "100%" }}
              >
                <Group position="apart" noWrap spacing="xs">
                  <Text size="xs" style={{ flex: 1 }}>
                    {v.label}
                  </Text>
                  {selected && (
                    <IconCheck
                      size={16}
                      stroke={2}
                      color={theme.colors.teal[6]}
                    />
                  )}
                </Group>
              </UnstyledButton>
            );
          })}
        </Box>
      </ScrollArea>
    </Box>
  );
}

function FilterChip({
  col,
  tableFilterQuery,
  onFilter,
}) {
  const { theme } = useStyles();
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);

  const { possibleValues, selectedValues, setSelectedValues } =
    useColumnDistinctFilter(col, tableFilterQuery);

  const chipText = getChipButtonLabel(
    col,
    selectedValues,
    possibleValues,
    t,
  );

  const pv = possibleValues ?? [];
  const sv = selectedValues ?? [];

  const isNoneSelected = pv.length > 0 && sv.length === 0;

  const isPartial =
    pv.length > 0 && sv.length > 0 && sv.length < pv.length;

  const handleFilter = (field, values, pValues) => {
    onFilter(field, values, pValues);
  };

  return (
    <Popover
      position="bottom-start"
      shadow="md"
      trapFocus={false}
      withinPortal
      opened={opened}
      onClose={() => setOpened(false)}
    >
      <Popover.Target>
        <Button
          variant="default"
          size="sm"
          radius="sm"
          compact
          onClick={() => setOpened((o) => !o)}
          rightIcon={
            opened ? (
              <IconChevronUp
                size={14}
                stroke={1.5}
                color={
                  isNoneSelected
                    ? theme.colors.red[4]
                    : undefined
                }
              />
            ) : (
              <IconChevronDown
                size={14}
                stroke={1.5}
                color={
                  isNoneSelected
                    ? theme.colors.red[4]
                    : undefined
                }
              />
            )
          }
          sx={
            isNoneSelected
              ? {
                  borderColor: theme.colors.red[5],
                  backgroundColor:
                    theme.colorScheme === "dark"
                      ? theme.fn.rgba(theme.colors.red[9], 0.22)
                      : theme.fn.rgba(theme.colors.red[0], 0.95),
                }
              : isPartial
                ? {
                    borderColor: theme.colors.teal[4],
                    backgroundColor:
                      theme.colorScheme === "dark"
                        ? theme.fn.rgba(theme.colors.teal[9], 0.2)
                        : theme.fn.rgba(theme.colors.teal[0], 0.95),
                  }
                : undefined
          }
        >
          <Text
            size="xs"
            weight={500}
            lineClamp={1}
            maw={220}
            color={isNoneSelected ? "red" : undefined}
          >
            {chipText}
          </Text>
        </Button>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <ColumnFilterPanel
          col={col}
          tableFilterQuery={tableFilterQuery}
          onFilter={handleFilter}
          possibleValues={possibleValues}
          selectedValues={selectedValues}
          setSelectedValues={setSelectedValues}
        />
      </Popover.Dropdown>
    </Popover>
  );
}

function LocalFilterChip({
  col,
  tableFilterQuery,
  onFilter,
  buildDistinct,
  dataSource,
}) {
  const { theme } = useStyles();
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);

  const { possibleValues, selectedValues, setSelectedValues } =
    useLocalDistinctFilter(col, tableFilterQuery, buildDistinct, dataSource);

  const chipText = getChipButtonLabel(
    col,
    selectedValues,
    possibleValues,
    t,
  );

  const pv = possibleValues ?? [];
  const sv = selectedValues ?? [];

  const isNoneSelected = pv.length > 0 && sv.length === 0;

  const isPartial =
    pv.length > 0 && sv.length > 0 && sv.length < pv.length;

  const handleFilter = (field, values, pValues) => {
    onFilter(field, values, pValues);
  };

  return (
    <Popover
      position="bottom-start"
      shadow="md"
      trapFocus={false}
      withinPortal
      opened={opened}
      onClose={() => setOpened(false)}
    >
      <Popover.Target>
        <Button
          variant="default"
          size="sm"
          radius="sm"
          compact
          onClick={() => setOpened((o) => !o)}
          rightIcon={
            opened ? (
              <IconChevronUp
                size={14}
                stroke={1.5}
                color={
                  isNoneSelected
                    ? theme.colors.red[4]
                    : undefined
                }
              />
            ) : (
              <IconChevronDown
                size={14}
                stroke={1.5}
                color={
                  isNoneSelected
                    ? theme.colors.red[4]
                    : undefined
                }
              />
            )
          }
          sx={
            isNoneSelected
              ? {
                  borderColor: theme.colors.red[5],
                  backgroundColor:
                    theme.colorScheme === "dark"
                      ? theme.fn.rgba(theme.colors.red[9], 0.22)
                      : theme.fn.rgba(theme.colors.red[0], 0.95),
                }
              : isPartial
                ? {
                    borderColor: theme.colors.teal[4],
                    backgroundColor:
                      theme.colorScheme === "dark"
                        ? theme.fn.rgba(theme.colors.teal[9], 0.2)
                        : theme.fn.rgba(theme.colors.teal[0], 0.95),
                  }
                : undefined
          }
        >
          <Text
            size="xs"
            weight={500}
            lineClamp={1}
            maw={220}
            color={isNoneSelected ? "red" : undefined}
          >
            {chipText}
          </Text>
        </Button>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <ColumnFilterPanel
          col={col}
          onFilter={handleFilter}
          possibleValues={possibleValues}
          selectedValues={selectedValues}
          setSelectedValues={setSelectedValues}
        />
      </Popover.Dropdown>
    </Popover>
  );
}

function LocalFilterChipBar({
  columns,
  tableFilterQuery,
  onFilterChange,
  buildDistinct,
  dataSource,
}) {
  const filterCols = columns.filter((c) => c.filterable && c.filter?.field);
  if (!filterCols.length) return null;

  return (
    <Box
      mb="md"
      sx={(theme) => ({
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
        paddingBottom: 4,
      })}
    >
      <Group spacing="xs" noWrap sx={{ flexWrap: "nowrap" }}>
        {filterCols.map((col, i) => (
          <LocalFilterChip
            key={`${col.field}-${i}`}
            col={col}
            tableFilterQuery={tableFilterQuery}
            onFilter={onFilterChange}
            buildDistinct={buildDistinct}
            dataSource={dataSource}
          />
        ))}
      </Group>
    </Box>
  );
}

function FilterChipBar({
  columns,
  tableFilterQuery,
  onFilterChange,
}) {
  const filterCols = columns.filter((c) => c.filterable && c.filter?.field);
  if (!filterCols.length) return null;

  return (
    <Box
      mb="md"
      sx={(theme) => ({
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
        paddingBottom: 4,
      })}
    >
      <Group spacing="xs" noWrap sx={{ flexWrap: "nowrap" }}>
        {filterCols.map((col, i) => (
          <FilterChip
            key={`${col.field}-${i}`}
            col={col}
            tableFilterQuery={tableFilterQuery}
            onFilter={onFilterChange}
          />
        ))}
      </Group>
    </Box>
  );
}

function Th({
  children,
  reversed,
  sorted,
  onSort,
  onFilter,
  col,
  tableFilterQuery,
  columnFilterLayout,
}) {
  const { classes } = useStyles();

  const { possibleValues, selectedValues, setSelectedValues } =
    useColumnDistinctFilter(col, tableFilterQuery);

  const showInlineFilter =
    col.filterable &&
    col.filter?.field &&
    columnFilterLayout === "header";

  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;
  return (
    <th
      className={classes.th}
      width={col.width}
      style={col.minWidth ? { minWidth: col.minWidth } : undefined}
    >
      <Group spacing={"xs"} noWrap>
        <UnstyledButton onClick={onSort} className={classes.control}>
          <Group position="apart" noWrap>
            <Text weight={500} size="xs">
              {children}
            </Text>
            <Center className={classes.icon}>
              <Icon size={14} stroke={1.5} />
            </Center>
          </Group>
        </UnstyledButton>
        {showInlineFilter && (
          <HoverCard shadow="md" width={col.filter.width ?? 200}>
            <HoverCard.Target>
              <Group spacing={1} noWrap>
                <ActionIcon variant="transparent" title="Filter" size="sm">
                  <IconFilter size={14} stroke={1.5} />
                </ActionIcon>
                {selectedValues?.length !== possibleValues?.length && (
                  <Text size="xs">({selectedValues?.length})</Text>
                )}
              </Group>
            </HoverCard.Target>
            <HoverCard.Dropdown p={0}>
              <ColumnFilterPanel
                col={col}
                onFilter={onFilter}
                possibleValues={possibleValues}
                selectedValues={selectedValues}
                setSelectedValues={setSelectedValues}
              />
            </HoverCard.Dropdown>
          </HoverCard>
        )}
      </Group>
    </th>
  );
}

const SearchBar = ({
  onChange = console.log,
  searchText,
  advanceFilters,
  onAdvancedFilterClick,
  onClearAdvancedFilters,
  hasAdvancedFilters,
  /** Mantine spacing size; use `xs` when filter chips sit directly below */
  groupMarginBottom = "md",
}) => {
  const { t } = useTranslation();
  return (
    <>
      <Group spacing="xs" mb={groupMarginBottom}>
        <TextInput
          placeholder={t("Search")}
          icon={<IconSearch size={14} stroke={1.5} />}
          value={searchText}
          clearable={true}
          onChange={(e) => {
            onChange(e.currentTarget.value);
          }}
          rightSection={
            searchText && (
              <UnstyledButton
                onClick={() => {
                  onChange("");
                }}
              >
                <IconX size={14} stroke={1.5} />{" "}
              </UnstyledButton>
            )
          }
          style={{ flex: 1 }}
        />
        {advanceFilters && advanceFilters.length > 0 && (
          <>
            <Button
              variant={hasAdvancedFilters ? "filled" : "default"}
              color="blue"
              size="sm"
              onClick={onAdvancedFilterClick}
              leftIcon={<IconFilter size={16} stroke={1.5} />}
              sx={
                !hasAdvancedFilters
                  ? {
                      borderColor: "#373A40",
                    }
                  : undefined
              }
            >
              {t("Filter")}
            </Button>
            {hasAdvancedFilters && (
              <Tooltip label={t("Clear Advanced Filters")}>
                <ActionIcon
                  variant="light"
                  color="red"
                  size="md"
                  onClick={onClearAdvancedFilters}
                  title={t("Clear Advanced Filters")}
                >
                  <IconX size={16} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
            )}
          </>
        )}
      </Group>
    </>
  );
};

const PaginationBar = ({
  pageSize,
  onPageSizeChange,
  pagination,
  onPageChange,
  currentPage,
}) => {
  useEffect(() => {
    pagination.page = currentPage;
  }, [currentPage]);
  const { t: translate } = useTranslation();
  return (
    <>
      {pagination && (
        <Group position="right">
          <Group>
            <Pagination
              size="xs"
              page={pagination.page}
              total={pagination.totalPage}
              siblings={1}
              onChange={onPageChange}
            />
            <Select
              data={[
                { value: 10, label: "10" },
                { value: 20, label: "20" },
                { value: 30, label: "30" },
                { value: 40, label: "40" },
                { value: 50, label: "50" },
              ]}
              value={pageSize}
              onChange={onPageSizeChange}
              size="xs"
            />
            <Text size="xs" color="dimmed">
              {pagination.total} {translate("Records")}
            </Text>
          </Group>
        </Group>
      )}
    </>
  );
};

const DataTable = (
  {
    data = [],
    columns = [],
    hidePagination = false,
    hideSearchBar = false,
    minHeight = "200px",
    pageSize = 10,
    pagination = { total: 10 },
    searchText = "",
    onPageSizeChange = () => {},
    onPageChange = () => {},
    onSearchChange = () => {},
    onSortChange = () => {},
    onFilterChange = () => {},
    onActionBtnClick = () => {},
    onRowDoubleClick = () => {},
    tableFilterQuery = {},
    currentPage = 1,
    padding = "xs",
    tableHeader,
    showRefreshButton = false,
    tdPaddingX = null,
    readonly = false,
    advanceFilters = [],
    onAdvancedFilterChange = () => {},
    advancedFilterQuery = {},
    columnFilterLayout = "toolbar",
    hideColumnHeaders = false,
    horizontalScroll = false,
    localFilterMode = false,
    buildDistinct = null,
    filterDataSource = null,
  },
  ref,
) => {
  useImperativeHandle(ref, () => {
    return {};
  });

  const [sortBy, setSortBy] = useState(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);

  const [rows, setRows] = useState([]);
  const [advancedFilterModalOpened, setAdvancedFilterModalOpened] =
    useState(false);

  const forceUpdate = useForceUpdate();

  const tableColumns = columns.filter((c) => !c.filterOnly);
  const filterableCols = columns.filter((c) => c.filterable && c.filter?.field);
  const showFilterToolbar =
    columnFilterLayout === "toolbar" && filterableCols.length > 0;
  const tableMinWidth = horizontalScroll
    ? tableColumns.reduce((sum, col) => sum + (col.minWidth || 120), 0)
    : undefined;

  useEffect(() => {
    const resizeObserverErrorHandler = (error) => {
      if (error.message.includes("ResizeObserver loop")) {
        console.warn("ResizeObserver loop error detected and ignored");
      } else {
        console.error(error);
      }
    };

    window.addEventListener("error", resizeObserverErrorHandler);

    return () => {
      window.removeEventListener("error", resizeObserverErrorHandler);
    };
  }, []);

  const setSorting = (field) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
  };

  const handleAdvancedFilterClick = () => {
    setAdvancedFilterModalOpened(true);
  };

  const handleAdvancedFilterApply = (query) => {
    onAdvancedFilterChange(query);
  };

  const handleAdvancedFilterClose = () => {
    setAdvancedFilterModalOpened(false);
  };

  const handleClearAdvancedFilters = () => {
    onAdvancedFilterChange({});
  };

  const hasActiveAdvancedFilters = () => {
    return Object.keys(advancedFilterQuery).length > 0;
  };

  const setDataRow = () => {
    if (!data) return;
    setRows(
      data?.map((d, index) => (
        <tr
          key={index}
          onDoubleClick={
            !onRowDoubleClick
              ? undefined
              : () => onRowDoubleClick({ data: d, rowIndex: index })
          }
          style={onRowDoubleClick ? { cursor: "pointer" } : undefined}
        >
          {tableColumns?.map((col, index_c) => (
            <td
              key={index_c}
              style={{
                verticalAlign: col.isAction ? "middle" : "top",
                textAlign: col.isAction ? "right" : undefined,
                whiteSpace: col.isAction || horizontalScroll ? "nowrap" : undefined,
                ...(col.minWidth ? { minWidth: col.minWidth } : {}),
                ...(tdPaddingX
                  ? { paddingLeft: tdPaddingX, paddingRight: tdPaddingX }
                  : {}),
              }}
            >
              {!col.cellRender && formatDisplayValue(_.get(d, col.field))}
              {col.cellRender && !col.isAction && col.cellRender(col, d)}
              {col.cellRender &&
                col.isAction &&
                col.cellRender(col, index, d, onActionBtnClick)}
            </td>
          ))}
        </tr>
      )),
    );
    forceUpdate();
  };
  useEffect(() => {
    setDataRow();
  }, [data, onRowDoubleClick, readonly, columns, horizontalScroll]);

  useEffect(() => {
    onSortChange(sortBy, reverseSortDirection);
  }, [sortBy, reverseSortDirection]);

  const { t } = useTranslation();

  const TableHeader = () => (
    <thead>
      <tr>
        {tableColumns.map((col, index) => {
          return col.sortable ? (
            <Th
              key={col.field + index}
              sorted={sortBy === col.field}
              reversed={reverseSortDirection}
              onSort={() => setSorting(col.field)}
              onFilter={(field, values, possibleValues) => {
                onFilterChange(field, values, possibleValues);
              }}
              col={col}
              tableFilterQuery={tableFilterQuery}
              columnFilterLayout={columnFilterLayout}
            >
              {!col.isAction &&
                t(col.headerName ?? _.capitalize(col.field))}
            </Th>
          ) : (
            <th
              key={col.field + index}
              style={col.minWidth ? { minWidth: col.minWidth } : undefined}
            >
              {!col.isAction &&
                t(col.headerName ?? _.capitalize(col.field))}
            </th>
          );
        })}
      </tr>
    </thead>
  );

  return (
    <>
      <Paper p={padding}>
        {!hideSearchBar && (
          <>
            <SearchBar
              onChange={onSearchChange}
              searchText={searchText}
              advanceFilters={advanceFilters}
              onAdvancedFilterClick={handleAdvancedFilterClick}
              onClearAdvancedFilters={handleClearAdvancedFilters}
              hasAdvancedFilters={hasActiveAdvancedFilters()}
              groupMarginBottom={showFilterToolbar ? "xs" : "md"}
            />
            {!showFilterToolbar && <Space h={"md"} />}
          </>
        )}
        {showFilterToolbar &&
          (localFilterMode ? (
            <LocalFilterChipBar
              columns={columns}
              tableFilterQuery={tableFilterQuery}
              onFilterChange={onFilterChange}
              buildDistinct={buildDistinct}
              dataSource={filterDataSource ?? data}
            />
          ) : (
            <FilterChipBar
              columns={columns}
              tableFilterQuery={tableFilterQuery}
              onFilterChange={onFilterChange}
            />
          ))}
        {tableHeader && <tableHeader.component {...tableHeader.props} />}
        {horizontalScroll ? (
          <ScrollArea type="auto" offsetScrollbars sx={{ width: "100%" }}>
            <Box sx={{ minWidth: tableMinWidth }}>
              <Table highlightOnHover fontSize={"xs"}>
                {!hideColumnHeaders && <TableHeader />}
                <tbody>{rows}</tbody>
              </Table>
            </Box>
          </ScrollArea>
        ) : (
          <Table highlightOnHover fontSize={"xs"}>
            {!hideColumnHeaders && <TableHeader />}
            <tbody>{rows}</tbody>
          </Table>
        )}
        {showRefreshButton && (
          <Button
            onClick={() => setDataRow()}
            size="xs"
            mt="xl"
            variant="light"
          >
            Refresh
          </Button>
        )}

        <Space h={"xl"} />
        {!hidePagination && (
          <PaginationBar
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            pagination={pagination}
            onPageChange={onPageChange}
            currentPage={currentPage}
          />
        )}
      </Paper>

      <AdvancedFilterModal
        opened={advancedFilterModalOpened}
        onClose={handleAdvancedFilterClose}
        advanceFilters={advanceFilters}
        onApplyFilters={handleAdvancedFilterApply}
        initialValues={advancedFilterQuery}
      />
    </>
  );
};

export default React.forwardRef(DataTable);
export {
  PaginationBar,
  FilterChipBar,
  LocalFilterChipBar,
  ColumnFilterPanel,
  getTableFilterSelectedValues,
};
